import { getWorkosAccessToken } from '../models/useAuth'
import { pushSettingsFrom, type PushCategoryId, type PushSetting } from '../utilities/pushPreferences'

/**
 * The router's push settings (`/v1/push/preferences`, nobgp-router src/push/preferences.go): which
 * categories this person turned off. Plain REST with the bearer, like lib/register.ts.
 */
const routerApiBaseUrl = import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

/** `unavailable`: the router answered 503, push is not switched on there yet. */
export type PushSettingsResult = { ok: true; settings: PushSetting[] } | { ok: false; reason: 'unavailable' | 'error' }

const call = async (method: 'GET' | 'PUT', body?: unknown): Promise<PushSettingsResult> => {
  const token = getWorkosAccessToken()
  if (!token) return { ok: false, reason: 'error' }
  try {
    const response = await fetch(`${routerApiBaseUrl}/v1/push/preferences`, {
      method,
      headers: { authorization: `Bearer ${token}`, ...(body ? { 'content-type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
    // ⚠ 404 is a router without the route (before the push release): the same answer as 503.
    if (response.status === 503 || response.status === 404) return { ok: false, reason: 'unavailable' }
    if (!response.ok) return { ok: false, reason: 'error' }
    const settings = pushSettingsFrom(await response.json().catch(() => null))
    return settings ? { ok: true, settings } : { ok: false, reason: 'error' }
  } catch {
    return { ok: false, reason: 'error' }
  }
}

export const fetchPushSettings = () => call('GET')

export const savePushSetting = (category: PushCategoryId, enabled: boolean) => call('PUT', { category, enabled })
